import { Injectable, inject } from '@angular/core';
import { CommandRegistryService } from '../../../../../../core/services/command-registry.service';
import type { CmdkItem } from '../../models/cmdk.model';
import type { CmdkProvider, CmdkProviderContext } from '../../models/cmdk-provider';
import { isNavCommand, toNavItem, visibleCommands } from './command.source';

/**
 * RESPONSIBILITY: the "Go to" scope — every navigable destination the viewer's
 * capabilities allow.
 *
 * Local and synchronous, zero network. Visibility comes from each command's
 * own `visible(ctx)` predicate, which for workspaces is
 * `isWorkspaceVisible(s, enabledFlags())` — an ANY-OF capability gate that
 * fails closed while the flag set is still loading. That is the behaviour we
 * want on a cold ⌘K: a destination the tenant does not have never flickers in.
 *
 * `idle: 'hide'` — the idle palette shows suggested actions, not the entire
 * destination list. The Go to chip (or the `g ` prefix) browses it in full.
 *
 * BACKEND CONTRACT: none.
 */
@Injectable({ providedIn: 'root' })
export class CmdkNavProvider implements CmdkProvider {
  private readonly registry = inject(CommandRegistryService);

  readonly id = 'nav';
  readonly kind = 'nav' as const;
  readonly scope = 'go-to' as const;
  readonly minQueryLength = 0;
  readonly idle = 'hide' as const;

  isEnabled(): boolean {
    return true;
  }

  /** The full destination catalogue, primary entries only. */
  browse(ctx: CmdkProviderContext): CmdkItem[] {
    return this.collect(ctx, true);
  }

  /**
   * The full destination catalogue, secondary entries included — the RANKER
   * does the matching, not the registry. See `visibleCommands` for why.
   */
  search(ctx: CmdkProviderContext): CmdkItem[] {
    return this.collect(ctx, false);
  }

  private collect(ctx: CmdkProviderContext, primaryOnly: boolean): CmdkItem[] {
    return visibleCommands(this.registry, ctx, { primaryOnly })
      .filter(isNavCommand)
      .map(toNavItem);
  }
}
